import { jsPDF } from 'jspdf';

const PAGE_WIDTH = 210;
const MARGIN_X = 14;
const CONTENT_WIDTH = PAGE_WIDTH - (MARGIN_X * 2);
const CONTENT_BOTTOM = 280;
const FONT_NAME = 'BitstreamVera';
const UNIVERSITY_NAME = 'KOCAELİ SAĞLIK VE TEKNOLOJİ ÜNİVERSİTESİ';

const safeFilePart = (value) => String(value || '')
  .trim()
  .replace(/[<>:"/\\|?*]/g, '')
  .replace(/\s+/g, ' ');

const arrayBufferToBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  let binary = '';
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + chunkSize));
  }
  return window.btoa(binary);
};

const loadPublicAsset = async (fileName) => {
  const publicPath = String(process.env.PUBLIC_URL || '').replace(/\/$/, '');
  const response = await fetch(`${publicPath}/${fileName}`);
  if (!response.ok) throw new Error(`PDF kaynağı yüklenemedi: ${fileName}`);
  return arrayBufferToBase64(await response.arrayBuffer());
};

const loadPdfAssets = async (pdf) => {
  const [regularFont, boldFont, logo] = await Promise.all([
    loadPublicAsset('fonts/Vera.ttf'),
    loadPublicAsset('fonts/VeraBd.ttf'),
    loadPublicAsset('kostu_logo.png'),
  ]);
  pdf.addFileToVFS('Vera.ttf', regularFont);
  pdf.addFont('Vera.ttf', FONT_NAME, 'normal');
  pdf.addFileToVFS('VeraBd.ttf', boldFont);
  pdf.addFont('VeraBd.ttf', FONT_NAME, 'bold');
  return `data:image/png;base64,${logo}`;
};

const fitFontSize = (pdf, text, maxWidth, initialSize, minimumSize = 7) => {
  let size = initialSize;
  pdf.setFontSize(size);
  while (size > minimumSize && pdf.getTextWidth(text) > maxWidth) {
    size -= 0.5;
    pdf.setFontSize(size);
  }
  return size;
};

const drawHeader = (pdf, programName, logoData, outcomeCount) => {
  pdf.setFillColor(15, 23, 42);
  pdf.rect(0, 0, PAGE_WIDTH, 40, 'F');
  pdf.setFillColor(69, 160, 73);
  pdf.rect(0, 38.5, PAGE_WIDTH, 1.5, 'F');

  pdf.setFillColor(255, 255, 255);
  pdf.roundedRect(11, 7, 45, 25, 2, 2, 'F');
  pdf.addImage(logoData, 'PNG', 13, 9, 41, 21, undefined, 'FAST');

  const textCenter = 130;
  const textWidth = 136;
  pdf.setFont(FONT_NAME, 'bold');
  fitFontSize(pdf, UNIVERSITY_NAME, textWidth, 11, 8);
  pdf.setTextColor(255, 255, 255);
  pdf.text(UNIVERSITY_NAME, textCenter, 13, { align: 'center' });

  pdf.setFontSize(12.5);
  pdf.text('NİHAİ PROGRAM ÇIKTILARI RAPORU', textCenter, 21, { align: 'center' });

  pdf.setFont(FONT_NAME, 'normal');
  pdf.setTextColor(203, 213, 225);
  const programLabel = programName || 'Program bilgisi belirtilmedi';
  fitFontSize(pdf, programLabel, textWidth, 9, 7);
  pdf.text(programLabel, textCenter, 28.5, { align: 'center' });

  pdf.setFont(FONT_NAME, 'bold');
  pdf.setFontSize(10.5);
  pdf.setTextColor(6, 24, 58);
  pdf.text('Program Çıktıları', MARGIN_X, 50);
  pdf.setFont(FONT_NAME, 'normal');
  pdf.setFontSize(8);
  pdf.setTextColor(100, 116, 139);
  pdf.text(
    `${outcomeCount} PÇ · Oluşturulma tarihi: ${new Date().toLocaleDateString('tr-TR')}`,
    PAGE_WIDTH - MARGIN_X,
    50,
    { align: 'right' },
  );
  pdf.setDrawColor(219, 226, 234);
  pdf.setLineWidth(0.35);
  pdf.line(MARGIN_X, 54, PAGE_WIDTH - MARGIN_X, 54);
  return 59;
};

const drawFooter = (pdf, pageNumber) => {
  pdf.setFont(FONT_NAME, 'normal');
  pdf.setFontSize(7.5);
  pdf.setTextColor(100, 116, 139);
  pdf.setDrawColor(219, 226, 234);
  pdf.setLineWidth(0.25);
  pdf.line(MARGIN_X, 283, PAGE_WIDTH - MARGIN_X, 283);
  pdf.text('Kocaeli Sağlık ve Teknoloji Üniversitesi · Program Çıktıları Raporu', MARGIN_X, 289);
  pdf.text(`Sayfa ${pageNumber}`, PAGE_WIDTH - MARGIN_X, 289, { align: 'right' });
};

const drawOutcome = (pdf, outcome, index, y) => {
  const textX = MARGIN_X + 17;
  const textWidth = CONTENT_WIDTH - 22;
  const lineHeight = 5.4;

  pdf.setFont(FONT_NAME, 'normal');
  pdf.setFontSize(9.5);
  const lines = pdf.splitTextToSize(outcome.name, textWidth);
  const rowHeight = Math.max(16, (lines.length * lineHeight) + 8);

  pdf.setFillColor(248, 250, 252);
  pdf.setDrawColor(219, 226, 234);
  pdf.setLineWidth(0.25);
  pdf.roundedRect(MARGIN_X, y, CONTENT_WIDTH, rowHeight, 2, 2, 'FD');

  pdf.setFillColor(15, 23, 42);
  pdf.roundedRect(MARGIN_X + 4, y + 4, 9, 9, 1.5, 1.5, 'F');
  pdf.setFont(FONT_NAME, 'bold');
  pdf.setFontSize(8.5);
  pdf.setTextColor(255, 255, 255);
  pdf.text(String(index + 1), MARGIN_X + 8.5, y + 10.2, { align: 'center' });

  pdf.setFont(FONT_NAME, 'normal');
  pdf.setFontSize(9.5);
  pdf.setTextColor(31, 41, 55);
  pdf.text(lines, textX, y + 7.2, { lineHeightFactor: 1.5 });
  return rowHeight;
};

export async function downloadProgramOutcomesPdf(outcomes, { programName = '' } = {}) {
  const cleanedOutcomes = (outcomes || []).map((outcome) => ({
    name: String(outcome?.name || '').trim(),
  }));
  if (cleanedOutcomes.length === 0) throw new Error('Program çıktısı bulunamadı.');
  if (cleanedOutcomes.some((outcome) => !outcome.name)) throw new Error('Program çıktısı boş bırakılamaz.');

  const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const logoData = await loadPdfAssets(pdf);
  pdf.setProperties({
    title: `${programName ? `${programName} - ` : ''}Nihai Program Çıktıları`,
    subject: 'Nihai Program Çıktıları Raporu',
    author: 'Kocaeli Sağlık ve Teknoloji Üniversitesi',
    creator: 'KOSTÜ Program Çıktıları Sistemi',
  });

  let pageNumber = 1;
  let y = drawHeader(pdf, programName, logoData, cleanedOutcomes.length);

  cleanedOutcomes.forEach((outcome, index) => {
    pdf.setFont(FONT_NAME, 'normal');
    pdf.setFontSize(9.5);
    const lines = pdf.splitTextToSize(outcome.name, CONTENT_WIDTH - 22);
    const rowHeight = Math.max(16, (lines.length * 5.4) + 8);

    if (y + rowHeight > CONTENT_BOTTOM) {
      drawFooter(pdf, pageNumber);
      pdf.addPage('a4', 'portrait');
      pageNumber += 1;
      y = drawHeader(pdf, programName, logoData, cleanedOutcomes.length);
    }

    y += drawOutcome(pdf, outcome, index, y) + 3;
  });

  drawFooter(pdf, pageNumber);
  const namePrefix = safeFilePart(programName);
  pdf.save(`${namePrefix ? `${namePrefix} - ` : ''}Nihai Program Çıktıları.pdf`);
}
